/**
 * Versicherte Gefahren — fixed catalog for the Immobilien contract.
 *
 * Jede Zeile hat eine statische Vorgabe (Höchstentschädigung /
 * Jahreshöchstentschädigung / Selbstbehalt laut Rahmenvereinbarung) und die
 * Eingaben, die der Sachbearbeiter je Gefahr in der Gefahren-Tabelle erfasst:
 *   - sollVersichertWerden (Ankreuzen)
 *   - hoechstentschaedigung / jahreshoechstentschaedigung / selbstbeteiligung (€)
 *   - mietverlust (ja/nein)
 *
 * Some rows have sub-rows (WE, IBS) which carry their own form_input.
 */

export type GefahrSubRow = {
  id: string;
  bezeichnung: string;
  hoechstentschaedigung: string;
  selbstbehalt: string;
};

export type GefahrRow = {
  id: string;
  versicherteGefahr: string;
  hoechstentschaedigung?: string;
  jahreshoechstentschaedigung: string;
  selbstbehalt?: string;
  /** Selbstbeteiligung Courté — additional column requested by Courté. */
  selbstbeteiligungCourte?: string;
  subRows?: GefahrSubRow[];
};

export const GEFAHREN_ROWS: GefahrRow[] = [
  {
    id: "feuer",
    versicherteGefahr: "Feuer (inkl. Überspannungsschäden durch Blitz)",
    hoechstentschaedigung: "keine",
    jahreshoechstentschaedigung: "keine",
    selbstbehalt: "kein",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "leitungswasser",
    versicherteGefahr: "Leitungswasser / Sprinklerleckage",
    hoechstentschaedigung: "keine",
    jahreshoechstentschaedigung: "keine",
    selbstbehalt: "kein",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "sturm_hagel",
    versicherteGefahr: "Sturm / Hagel",
    hoechstentschaedigung: "keine",
    jahreshoechstentschaedigung: "keine",
    selbstbehalt: "kein",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "we_elementar",
    versicherteGefahr: "Weitere Elementargefahren (WE)",
    jahreshoechstentschaedigung: "100% der VS, max. € 10 Mio.",
    selbstbeteiligungCourte: "—",
    subRows: [
      {
        id: "we_a",
        bezeichnung: "a) Erdsenkung, Erdrutsch, Schneedruck, Lawinen, Vulkanausbruch",
        hoechstentschaedigung: "100 % der VS, max. 2,5 Mio. EUR (RV max. 10 Mio. €)",
        selbstbehalt: "250 €",
      },
      {
        id: "we_b",
        bezeichnung: "b) Überschwemmung, Starkregen, Rückstau",
        hoechstentschaedigung: "100 % der VS, max. 2,5 Mio. EUR (RV max. 10 Mio. €)",
        selbstbehalt: "1.000 € (Reduz. auf 500 € möglich)",
      },
      {
        id: "we_c",
        bezeichnung: "c) Erdbeben, Erdfall, Erdrutsch, Erdsenkung",
        hoechstentschaedigung: "100 % der VS, max. 2,5 Mio. EUR (RV max. 10 Mio. €)",
        selbstbehalt: "1.000 € (Reduz. auf 500 € möglich)",
      },
    ],
  },
  {
    id: "ibs",
    versicherteGefahr: "IBS (Innere Unruhen, Böswillige Beschädigung, Streik, Aussperrung)",
    jahreshoechstentschaedigung: "100% der VS, max. € 10 Mio.",
    selbstbeteiligungCourte: "—",
    subRows: [
      {
        id: "ibs_a",
        bezeichnung: "A) Innere Unruhen, Böswillige Beschädigung, Streik und Aussperrung",
        hoechstentschaedigung: "100 % der VS, max. 2,5 Mio. EUR (RV max. 10 Mio. €)",
        selbstbehalt: "250 €",
      },
      {
        id: "ibs_b",
        bezeichnung: "B) Graffiti",
        hoechstentschaedigung: "100 % der VS, max. 2,5 Mio. EUR (RV max. 10 Mio. €)",
        selbstbehalt: "250 €",
      },
    ],
  },
  {
    id: "fr",
    versicherteGefahr: "Fahrzeuganprall, Rauch und Überschalldruckwellen (FR)",
    hoechstentschaedigung: "100 % der VS, max. 2,5 Mio. EUR (RV max. 10 Mio. €)",
    jahreshoechstentschaedigung: "100% der VS, max. € 10 Mio.",
    selbstbehalt: "250 €",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "ug",
    versicherteGefahr: "Unbenannte Gefahren (UG)",
    hoechstentschaedigung: "100 % der VS, max. 2,5 Mio. EUR (RV max. 10 Mio. €)",
    jahreshoechstentschaedigung: "100% der VS, max. € 10 Mio.",
    selbstbehalt: "250 €",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "wug",
    versicherteGefahr: "Weitere Unbenannte Gefahren (WUG)",
    hoechstentschaedigung: "siehe UG",
    jahreshoechstentschaedigung: "100% der VS, max. € 10 Mio.",
    selbstbehalt: "250 €",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "glas_allgemein",
    versicherteGefahr: "Glasbruch – Allgemeinverglasung",
    hoechstentschaedigung: "keine",
    jahreshoechstentschaedigung: "keine",
    selbstbehalt: "kein",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "glas_gesamt_ohne_schaufenster",
    versicherteGefahr: "Glasbruch – Gesamtverglasung ohne Schaufenster",
    hoechstentschaedigung: "keine",
    jahreshoechstentschaedigung: "keine",
    selbstbehalt: "kein",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "glas_gesamt_inkl_schaufenster",
    versicherteGefahr: "Glasbruch – Gesamtverglasung inkl. Schaufenster",
    hoechstentschaedigung: "keine",
    jahreshoechstentschaedigung: "keine",
    selbstbehalt: "kein",
    selbstbeteiligungCourte: "—",
  },
  {
    id: "tv",
    versicherteGefahr: "Gefahren der Technischen Versicherung (TV) gemäß Klausel VSG/D 040150/16",
    hoechstentschaedigung: "50.000 € auf Erstes Risiko (Erhöhung möglich)",
    jahreshoechstentschaedigung: "keine",
    selbstbehalt: "100 € (bei Daten: 10 % mind. 500 EUR)",
    selbstbeteiligungCourte: "—",
  },
];

export type GefahrFormInput = {
  /** Angekreuzt: Diese Gefahr soll versichert werden. */
  sollVersichertWerden?: "ja" | "nein";
  /** Höchstentschädigung in € — nur Ziffern, Anzeige mit Tausenderpunkten. */
  hoechstentschaedigung?: string;
  /** Jahreshöchstentschädigung in € — nur Ziffern, Anzeige mit Tausenderpunkten. */
  jahreshoechstentschaedigung?: string;
  /** Selbstbeteiligung in € — nur Ziffern. */
  selbstbeteiligung?: string;
  /** Soll für diese Gefahr ein Mietverlust vereinbart werden? */
  mietverlust?: "ja" | "nein";
};

/** Die drei Euro-Felder je Gefahr — Schlüssel für generische Zellen. */
export type GefahrBetragKey =
  | "hoechstentschaedigung"
  | "jahreshoechstentschaedigung"
  | "selbstbeteiligung";

/** Reduziert eine Eingabe auf reine Ziffern (der Speicher-/Rechenwert). */
export const onlyDigits = (raw: string): string => raw.replace(/[^0-9]/g, "");

/** Formatiert reine Ziffern als Betrag mit Tausenderpunkten ("2500000" → "2.500.000"). */
export const formatThousands = (digits: string): string => {
  const d = onlyDigits(digits);
  if (!d) return "";
  return d.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

/** Map of input rows: top-level rowId or subRowId → form input */
export type GefahrenFormState = Record<string, GefahrFormInput>;

/** Returns true if at least one row is checked as "soll versichert werden". */
export const anyVersichert = (state: GefahrenFormState): boolean => {
  return Object.values(state).some((v) => v?.sollVersichertWerden === "ja");
};

/** Returns true if at least one insured row has Mietverlust = ja. */
export const anyMietverlust = (state: GefahrenFormState): boolean => {
  return Object.values(state).some(
    (v) => v?.sollVersichertWerden === "ja" && v?.mietverlust === "ja",
  );
};

/** Returns the IDs of all rows currently selected (sollVersichertWerden === "ja"). */
export const selectedGefahrIds = (state: GefahrenFormState): string[] => {
  return Object.entries(state)
    .filter(([, v]) => v?.sollVersichertWerden === "ja")
    .map(([id]) => id);
};

/** Returns the IDs of all insured rows with Mietverlust = ja. */
export const mietverlustGefahrIds = (state: GefahrenFormState): string[] => {
  return Object.entries(state)
    .filter(([, v]) => v?.sollVersichertWerden === "ja" && v?.mietverlust === "ja")
    .map(([id]) => id);
};

/** Look up the user-friendly label for a Gefahr ID (top-level or sub-row). */
export const labelForGefahrId = (id: string): string => {
  for (const r of GEFAHREN_ROWS) {
    if (r.id === id && (!r.subRows || r.subRows.length === 0)) return r.versicherteGefahr;
    if (r.subRows) {
      const sub = r.subRows.find((s) => s.id === id);
      if (sub) return `${r.versicherteGefahr} — ${sub.bezeichnung}`;
    }
  }
  return id;
};

/** Iterate every input row id (top-level rows without subRows + sub-row ids). */
export const iterateInputRows = (): Array<{ id: string; label: string; parentId?: string }> => {
  const rows: Array<{ id: string; label: string; parentId?: string }> = [];
  for (const r of GEFAHREN_ROWS) {
    if (r.subRows && r.subRows.length) {
      for (const sub of r.subRows) {
        rows.push({ id: sub.id, label: `${r.versicherteGefahr} — ${sub.bezeichnung}`, parentId: r.id });
      }
    } else {
      rows.push({ id: r.id, label: r.versicherteGefahr });
    }
  }
  return rows;
};

/** Kurzfassung der Vorgaben (HE / Selbstbehalt) für die Tabellen-Hilfszeile. */
export const vorgabeHint = (spec: {
  hoechstentschaedigung?: string;
  jahreshoechstentschaedigung?: string;
  selbstbehalt?: string;
}): string => {
  const parts: string[] = [];
  if (spec.hoechstentschaedigung && !/^keine$/i.test(spec.hoechstentschaedigung.trim())) {
    parts.push(`HE ${spec.hoechstentschaedigung}`);
  }
  if (spec.jahreshoechstentschaedigung && !/^keine$/i.test(spec.jahreshoechstentschaedigung.trim())) {
    parts.push(`JHE ${spec.jahreshoechstentschaedigung}`);
  }
  if (spec.selbstbehalt && !/^kein$/i.test(spec.selbstbehalt.trim())) {
    parts.push(`SB ${spec.selbstbehalt}`);
  }
  return parts.length ? `Vorgabe: ${parts.join(" · ")}` : "";
};
